import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import axios from 'axios';
import { contactsService } from '../services/contactsService';
import type { CreateContactRequest } from '@troia-v3/types';

/**
 * Mensagem que o SERVIDOR mandou, quando ele mandou alguma.
 *
 * O `errorHandler` do backend responde `{ success, code, message }` para toda a
 * hierarquia `AppError` — é ali que mora o motivo legível (403 sem permissão,
 * 409 da trava de exclusão, 422 de domínio). Sem ler esse campo, o atendente
 * clica, confirma e não acontece nada: a falha some sem aviso nenhum.
 */
function serverMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error) && error.response?.data) {
    const data = error.response.data as { message?: string; error?: string };
    return data.message || data.error || fallback;
  }
  return fallback;
}

/**
 * Hook para criar um novo contato
 */
export const useCreateContact = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateContactRequest) => contactsService.createContact(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
};

/**
 * Hook para atualizar um contato existente
 */
export const useUpdateContact = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateContactRequest> }) =>
      contactsService.updateContact(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
};

/**
 * Hook para deletar um contato
 */
export const useDeleteContact = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => contactsService.deleteContact(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
};

/**
 * Bloquear contato (2026-09-01). Invalida `conversations` porque o bloqueio
 * FECHA as conversas abertas do contato no servidor — sem invalidar, a conversa
 * some da fila só no próximo refetch. Invalida `conversation` para a faixa
 * aparecer sem esperar o staleTime, e `contacts` para o selo aparecer na aba.
 */
export function useBlockContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => contactsService.blockContact(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      // Singular, e não é o mesmo prefixo do plural: `['contact', id]` é a chave
      // do contato avulso (`useContact`), a que o Histórico usa para decidir se
      // mostra o botão de desbloquear — e ela tem `staleTime` de 5 minutos. Sem
      // esta linha, quem desbloqueia pelo Histórico continua vendo o botão como
      // se nada tivesse acontecido (defeito flagrado pelo teste de navegador).
      queryClient.invalidateQueries({ queryKey: ['contact'] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      queryClient.invalidateQueries({ queryKey: ['conversation'] });
      toast.success('Contato bloqueado');
    },
    onError: (error) => {
      console.error('Erro ao bloquear contato:', error);
      toast.error(serverMessage(error, 'Erro ao bloquear contato'));
    },
  });
}

export function useUnblockContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => contactsService.unblockContact(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      // Ver o comentário em `useBlockContact`: é esta chave que o Histórico lê.
      queryClient.invalidateQueries({ queryKey: ['contact'] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      queryClient.invalidateQueries({ queryKey: ['conversation'] });
      toast.success('Contato desbloqueado');
    },
    onError: (error) => {
      console.error('Erro ao desbloquear contato:', error);
      toast.error(serverMessage(error, 'Erro ao desbloquear contato'));
    },
  });
}
